import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getClients,
  createClient,
  updateClient,
  deleteClient,
} from '../../services/clientService';
import { createFile } from '../../services/fileService';
import ClientModal from '../../components/modals/ClientModal';
import PassengerModal from '../../components/modals/PassengerModal';
import CreateFileModal from '../../components/modals/CreateFileModal';
import './Reservations.css';
import '../../components/common/IconButton.css';
import Table from '../../components/common/Table';
import { formatCuil, formatDni } from '../../utils/formatters';
import { IconFilePlus, IconPencil, IconUsers } from '../../components/common/Icons';

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [queryFilter, setQueryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [passengerModalOpen, setPassengerModalOpen] = useState(false);
  const [createFileModalOpen, setCreateFileModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientForFile, setSelectedClientForFile] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getClients();
      setClients(data);
    } catch (err) {
      console.error(err);
      setError('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const sortedClients = useMemo(() => {
    const q = queryFilter.toLowerCase();
    const filtered = clients.filter((c) =>
      `${c.surname} ${c.name} ${c.dni} ${c.cuil ?? ''} ${formatCuil(c.cuil)} ${c.mail ?? ''}`.toLowerCase().includes(q)
    );

    return filtered;
  }, [clients, queryFilter]);

  const handleCreate = () => {
    setSelectedClient(null);
    setClientModalOpen(true);
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setClientModalOpen(true);
  };

  const handleSaveClient = async (data) => {
    try {
      if (selectedClient) {
        await updateClient(selectedClient.id, data);
      } else {
        await createClient(data);
      }
      setClientModalOpen(false);
      await loadClients();
    } catch (err) {
      console.error(err);
      setError('No se pudo guardar el cliente');
    }
  };

  const handleDeleteClient = async (id) => {
    try {
      await deleteClient(id);
      setClientModalOpen(false);
      await loadClients();
    } catch (err) {
      console.error(err);
      setError('No se pudo eliminar el cliente');
    }
  };

  const handlePassengers = (client) => {
    setSelectedClient(client);
    setPassengerModalOpen(true);
  };

  const handleOpenCreateFileModal = (client) => {
    setError('');
    setSelectedClientForFile(client);
    setCreateFileModalOpen(true);
  };

  const handleConfirmCreateFile = async ({ clientId, dateOut, dateIn }) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const file = await createFile({
        clientId,
        creationDate: today,
        dateOut,
        dateIn,
        status: 'Incompleto'
      });
      setCreateFileModalOpen(false);
      setSelectedClientForFile(null);
      navigate(`/backoffice/ficha/${file.id}`);
    } catch (err) {
      console.error(err);
      setError('No se pudo crear la ficha');
    }
  };

  const columns = [
    {
      id: 'surname',
      header: 'Apellido',
      accessor: (c) => c.surname || '',
      filterPlaceholder: 'Filtrar apellido'
    },
    {
      id: 'name',
      header: 'Nombre',
      accessor: (c) => c.name || '',
      filterPlaceholder: 'Filtrar nombre'
    },
    {
      id: 'cuil',
      header: 'CUIL',
      accessor: (c) => formatCuil(c.cuil) || '',
      filterPlaceholder: 'Filtrar CUIL'
    },
    {
      id: 'dni',
      header: 'DNI',
      accessor: (c) => formatDni(c.dni) || '',
      filterPlaceholder: 'Filtrar DNI'
    },
    {
      id: 'mail',
      header: 'Mail',
      accessor: (c) => c.mail || '-',
      filterPlaceholder: 'Filtrar mail'
    },
    {
      id: 'phone',
      header: 'Teléfono',
      accessor: (c) => c.phone || '-',
      filterPlaceholder: 'Filtrar teléfono'
    },
    {
      id: 'actions',
      header: 'Acciones',
      headerClassName: 'th-center',
      sortable: false,
      filterable: false,
      cell: (client) => (
        <div className="inline-actions">
          <button
            type="button"
            className="icon-btn icon-btn--edit"
            onClick={() => handleEdit(client)}
            aria-label="Editar cliente"
            title="Editar"
          >
            <IconPencil />
          </button>
          <button
            type="button"
            className="icon-btn icon-btn--edit"
            onClick={() => handlePassengers(client)}
            aria-label="Ver pasajeros"
            title="Ver pasajeros"
          >
            <IconUsers />
          </button>
          <button
            type="button"
            className="icon-btn icon-btn--select"
            onClick={() => handleOpenCreateFileModal(client)}
            aria-label="Crear ficha"
            title="Crear ficha"
          >
            <IconFilePlus />
          </button>
        </div>
      )
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <h1>Clientes</h1>
        <div className="actions-row">
          <input
            className="search-input"
            placeholder="Buscar cliente"
            value={queryFilter}
            onChange={(e) => setQueryFilter(e.target.value)}
          />
          <button className="btn" onClick={handleCreate}>+ Nuevo Cliente</button>
        </div>
      </div>

      {error && <div className="form-error-message" role="alert">{error}</div>}
      {loading ? (
        <p>Cargando...</p>
      ) : (
        <Table
          data={sortedClients}
          columns={columns}
          rowKey="id"
          className="table"
          emptyMessage="No hay clientes para mostrar."
        />
      )}

      <ClientModal
        isOpen={clientModalOpen}
        onClose={() => setClientModalOpen(false)}
        onSave={handleSaveClient}
        onDelete={handleDeleteClient}
        initialData={selectedClient}
      />

      <PassengerModal
        isOpen={passengerModalOpen}
        onClose={() => setPassengerModalOpen(false)}
        client={selectedClient}
        onUpdated={loadClients}
      />

      <CreateFileModal
        isOpen={createFileModalOpen}
        onClose={() => {
          setCreateFileModalOpen(false);
          setSelectedClientForFile(null);
        }}
        onConfirm={handleConfirmCreateFile}
        initialClientId={selectedClientForFile?.id}
        clientSelectDisabled
      />
    </div>
  );
};

export default ClientsPage;
